function jsonEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

const IGNORED_JSON_KEYS = new Set(['__typename']);

const USER_EDITABLE_KEYS = [
  'title',
  'description',
  'qrCodeCta',
  'startTime',
  'endTime',
  'order',
  'config',
  'data',
] as const;

export type AdminEngagementUpdateSnapshot = {
  config?: unknown;
  data?: unknown;
  description?: string | null;
  endTime?: unknown;
  order?: number | null;
  qrCodeCta?: string | null;
  startTime?: unknown;
  title?: string | null;
  [key: string]: unknown;
};

/**
 * Build an Admin auto-save payload. Live `data` keys (queues, votes, raffle
 * state) are omitted unless they actually changed in the editor, and even then
 * only the changed keys are sent. The server still allowlists admin-owned keys.
 */
export function fullEngagementToUpdateInput(
  engagement: AdminEngagementUpdateSnapshot,
  previous?: Pick<AdminEngagementUpdateSnapshot, 'data'>,
) {
  const input: {
    config: unknown;
    description?: string | null;
    endTime?: unknown;
    order?: number | null;
    qrCodeCta?: string | null;
    startTime?: unknown;
    title?: string | null;
    data?: Record<string, unknown>;
  } = {
    config: engagement.config,
    description: engagement.description,
    endTime: engagement.endTime,
    order: engagement.order,
    qrCodeCta: engagement.qrCodeCta,
    startTime: engagement.startTime,
    title: engagement.title,
  };

  const dataPatch = pickChangedJsonFields(previous?.data, engagement.data);
  if (dataPatch) {
    input.data = dataPatch;
  }

  return input;
}

export function pickChangedJsonFields(
  previous: unknown,
  next: unknown,
): Record<string, unknown> | undefined {
  if (!next || typeof next !== 'object' || Array.isArray(next)) {
    return undefined;
  }
  const prev =
    previous && typeof previous === 'object' && !Array.isArray(previous)
      ? (previous as Record<string, unknown>)
      : {};
  const nextRecord = next as Record<string, unknown>;
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(nextRecord)) {
    if (IGNORED_JSON_KEYS.has(key)) {
      continue;
    }
    if (!jsonEqual(prev[key], value)) {
      patch[key] = value;
    }
  }
  return Object.keys(patch).length > 0 ? patch : undefined;
}

/**
 * Fold a server/cache engagement into the editor without treating live fields
 * as user edits. Dirty local fields are kept; clean fields take the server
 * value and become the new saved baseline.
 */
export function applyServerEngagementPush({
  editor,
  lastSaved,
  incoming,
}: {
  editor: AdminEngagementUpdateSnapshot;
  lastSaved: AdminEngagementUpdateSnapshot;
  incoming: AdminEngagementUpdateSnapshot;
}): {
  nextEditor: AdminEngagementUpdateSnapshot;
  nextLastSaved: AdminEngagementUpdateSnapshot;
  didChange: boolean;
} {
  const nextEditor: Record<string, unknown> = {
    ...incoming,
    editorMode: editor.editorMode,
  };
  const nextLastSaved: Record<string, unknown> = {
    ...incoming,
    editorMode: editor.editorMode,
  };
  for (const key of USER_EDITABLE_KEYS) {
    nextEditor[key] = mergeEditorValue(
      editor[key],
      lastSaved[key],
      incoming[key],
    );
    nextLastSaved[key] = mergeSavedBaseline(
      editor[key],
      lastSaved[key],
      incoming[key],
    );
  }
  return {
    nextEditor,
    nextLastSaved,
    didChange: !jsonEqual(nextEditor, editor),
  };
}

function mergeEditorValue(
  editorValue: unknown,
  savedValue: unknown,
  incomingValue: unknown,
): unknown {
  const editorIsRecord = isPlainRecord(editorValue);
  const incomingIsRecord = isPlainRecord(incomingValue);
  if (editorIsRecord && incomingIsRecord) {
    const savedRecord = isPlainRecord(savedValue) ? savedValue : {};
    const keys = new Set([
      ...Object.keys(editorValue),
      ...Object.keys(incomingValue),
    ]);
    const merged: Record<string, unknown> = { ...incomingValue };
    for (const key of keys) {
      if (IGNORED_JSON_KEYS.has(key)) {
        merged[key] = incomingValue[key] ?? editorValue[key];
        continue;
      }
      merged[key] = jsonEqual(editorValue[key], savedRecord[key])
        ? incomingValue[key]
        : editorValue[key];
    }
    return merged;
  }
  return jsonEqual(editorValue, savedValue) ? incomingValue : editorValue;
}

function mergeSavedBaseline(
  editorValue: unknown,
  savedValue: unknown,
  incomingValue: unknown,
): unknown {
  const editorIsRecord = isPlainRecord(editorValue);
  const incomingIsRecord = isPlainRecord(incomingValue);
  if (editorIsRecord && incomingIsRecord) {
    const savedRecord = isPlainRecord(savedValue) ? savedValue : {};
    const keys = new Set([
      ...Object.keys(editorValue),
      ...Object.keys(incomingValue),
    ]);
    const merged: Record<string, unknown> = { ...incomingValue };
    for (const key of keys) {
      if (IGNORED_JSON_KEYS.has(key)) {
        merged[key] = incomingValue[key] ?? savedRecord[key];
        continue;
      }
      merged[key] = jsonEqual(editorValue[key], savedRecord[key])
        ? incomingValue[key]
        : savedRecord[key];
    }
    return merged;
  }
  return jsonEqual(editorValue, savedValue) ? incomingValue : savedValue;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function stripTypename(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripTypename);
  if (!isPlainRecord(value)) return value === undefined ? null : value;
  const next: Record<string, unknown> = {};
  for (const [key, nested] of Object.entries(value)) {
    if (key === '__typename') continue;
    next[key] = stripTypename(nested);
  }
  return next;
}

const SAVE_COMPARE_KEYS = [
  'title',
  'description',
  'qrCodeCta',
  'startTime',
  'endTime',
  'order',
  'config',
  'data',
] as const;

/**
 * Comparable editor payload: user-owned fields only, no GraphQL identities,
 * timestamps, or view projections. A server echo of the last-sent config
 * must compare equal even when the response object is a new identity.
 */
export function editorSaveCompareValue(
  engagement: AdminEngagementUpdateSnapshot,
) {
  const next: Record<string, unknown> = {};
  for (const key of SAVE_COMPARE_KEYS) {
    next[key] = stripTypename(engagement[key] ?? null);
  }
  return next;
}

export function shouldQueueAutosave(
  editor: AdminEngagementUpdateSnapshot,
  lastSaved: AdminEngagementUpdateSnapshot,
) {
  return !jsonEqual(
    editorSaveCompareValue(editor),
    editorSaveCompareValue(lastSaved),
  );
}
